/**
 * DynamicDateInput — single-control date input matching Flutter's
 * `DynamicDatePicker` (PROD-827 chart write surfaces).
 *
 * Flutter's clinical chart forms (allergies, problems, immunizations…)
 * use a single field that accepts ANY of:
 *
 *   - `DD/MM/YYYY`  → full date
 *   - `MM/YYYY`     → month + year only
 *   - `YYYY`        → year only
 *   - `1Y` / `2M` / `3D`  → duration ago from today (years/months/days)
 *
 * Plus a calendar icon button on the right opens a date picker as an
 * alternative. The control emits TWO values from one source:
 *
 *   - `onset`        → ISO `YYYY-MM-DDTHH:mm:ss.sss` (no trailing Z)
 *   - `onSetDisplay` → the user's typed string, preserved verbatim
 *
 * These two fields ship together on the wire (Allergies' `onset` /
 * `onSetDisplay`, Conditions' `onsetDateTime` / `onsetDisplayDateTime`).
 *
 * **Why ONE control, not two side-by-side inputs**: prior slice shipped
 * a date-picker + free-text dual input which Andrew's 2026-05-19 design
 * review flagged as wrong (Flutter parity is one control). This single
 * source of truth means the two emitted fields can't drift, and the
 * UX matches the dev environment.
 *
 * The label-side info `<Tooltip>` quotes Flutter's helper copy
 * verbatim: "Enter date (DD/MM/YYY, MM/YYYY, YYYY or duration eg:
 * 1Y or 2M or 3D)".
 */

import { useId, useMemo, useState } from 'react';
import { CalendarIcon, Info } from 'lucide-react';
import {
  DatePickerDialog,
  FieldLabel,
  TextField,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scripsteam/scrips-signal-ds';

export interface DynamicDateInputProps {
  /** UI label, e.g. "Onset date" / "Start date" / "Vaccination date". */
  label: string;
  required?: boolean;
  /** What the user typed verbatim — e.g. "2024", "May 2024", "1Y". */
  displayValue: string;
  /** ISO date with no trailing Z — `YYYY-MM-DDTHH:mm:ss.sss`. May be
   *  null if the user's free-text didn't parse. */
  isoValue: string | null;
  /** Called with both resolved fields whenever either input changes.
   *  The parent should write both to form state — they ship together. */
  onChange: (next: { displayValue: string; isoValue: string | null }) => void;
  /** Maximum allowable date for the calendar picker. Defaults to today. */
  max?: Date;
  /** Validation error message; renders red beneath the field. */
  error?: string;
  /** Disable the entire control. */
  disabled?: boolean;
  /** Optional input id for `htmlFor` wiring. */
  id?: string;
  /** Override the helper text. Defaults to the Flutter wording. */
  helperText?: string;
  /** Caption of the dialog's month grid, forwarded to the DS dialog.
   *  `'label'` (the default, and the deployed app's shape) shows the month
   *  name with prev/next arrows. `'dropdown'` adds month + year selects —
   *  set it only where a far-past or far-future date is genuinely expected
   *  (a 1960 family-history onset, a 2030 vaccine expiry), because reaching
   *  a distant year through the arrows alone costs hundreds of clicks. */
  captionLayout?: 'label' | 'dropdown';
}

const FLUTTER_HELPER =
  'Enter date (DD/MM/YYYY, MM/YYYY, YYYY or duration eg: 1Y or 2M or 3D)';

export function DynamicDateInput({
  label,
  required = false,
  displayValue,
  isoValue,
  onChange,
  max,
  error,
  disabled = false,
  id,
  helperText = FLUTTER_HELPER,
  captionLayout = 'label',
}: DynamicDateInputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [pickerOpen, setPickerOpen] = useState(false);

  // The dialog's selected Date is derived from isoValue when it parses;
  // otherwise it opens with nothing drafted.
  //
  // ⚠️ TWO CONVENTIONS MEET HERE — read this before touching either end.
  //
  // This module is UTC-internal on the WIRE: `buildDate` builds via `Date.UTC`,
  // and `toIsoNoZ` / `formatDdMmYyyy` read `getUTC*`. A calendar grid, however,
  // speaks LOCAL days: react-day-picker renders each cell as a local-midnight
  // Date, compares the selection with `isSameDay`, and hands a local-midnight
  // Date back on click. So the boundary must translate, and it must translate
  // in BOTH directions or one hemisphere breaks:
  //
  //   IN  — `new Date(isoValue)` is CORRECT here and must stay. A `Z`-less ISO
  //         parses as LOCAL time, so this is the local midnight of the stored
  //         calendar day — exactly the shape the grid highlights by. Measured
  //         equal to `new Date(y, m-1, d)` in both hemispheres. Do NOT "fix" it
  //         to `Date.UTC` midnight to match the wire: that reads back correctly
  //         east of UTC but rings the WRONG DAY west of it (measured:
  //         `Date.UTC(2024,4,19)` shows day 18 in America/Chicago).
  //   OUT — re-anchor the clicked local day at UTC midnight, in
  //         `handlePickerSelect` below. That is the half that was MISSING, and
  //         the whole of the defect.
  //
  // The defect: east of UTC every date taken from the picker was recorded one
  // day early, because the local instant was read back with `getUTC*`. And
  // since the dialog seeds its draft from `value` and re-emits it on OK, merely
  // opening the picker and confirming moved a stored date backwards. Measured at
  // UTC+3 before the fix: `2024-05-19T00:00:00.000` came back
  // `2024-05-18T21:00:00.000` / "18/05/2024".
  // Pinned in both hemispheres by `dynamic-date-input.test.tsx` (UTC+4) and
  // `dynamic-date-input.tz-west.test.tsx` (UTC-5); the two files fail in
  // OPPOSITE directions, so a change that trades one for the other is caught.
  const selectedDate = useMemo<Date | undefined>(() => {
    if (!isoValue) return undefined;
    const d = new Date(isoValue);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [isoValue]);

  const handleTextChange = (next: string) => {
    const parsed = parseDynamicDate(next, max ?? new Date());
    onChange({ displayValue: next, isoValue: parsed });
  };

  const handlePickerSelect = (d: Date | undefined) => {
    if (!d) {
      onChange({ displayValue: '', isoValue: null });
      setPickerOpen(false);
      return;
    }
    // OUT half of the convention translation documented on `selectedDate`:
    // the grid hands back the clicked day as LOCAL midnight, so re-anchor that
    // calendar day at UTC midnight before it reaches the UTC-reading helpers.
    // Without this, `formatDdMmYyyy`/`toIsoNoZ` read the local instant in UTC
    // and record the previous day everywhere east of UTC.
    const day = buildDate(d.getFullYear(), d.getMonth() + 1, d.getDate()) ?? d;
    onChange({
      displayValue: formatDdMmYyyy(day),
      isoValue: toIsoNoZ(day),
    });
    setPickerOpen(false);
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <FieldLabel
          htmlFor={inputId}
          
        >
          {label}
          {required ? <span className="text-red-700 ml-1">*</span> : null}
        </FieldLabel>
        {/* Tooltips open and close at once (design log 13 Oct, Rule "tooltips"). */}
        <TooltipProvider delay={0} closeDelay={0}>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={`${label} formats`}
                  className="inline-flex items-center justify-center h-4 w-4 text-brand-primary hover:text-brand-depth rounded-full"
                />
              }
            >
              <Info className="size-4" aria-hidden />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-65">
              {helperText}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="relative mt-1">
        <TextField
          id={inputId}
          type="text"
          inputMode="text"
          autoComplete="off"
          placeholder="dd/mm/yyyy"
          value={displayValue}
          onChange={(e) => handleTextChange(e.target.value)}
          disabled={disabled}
          status={error ? 'error' : 'default'}
          // `inputClassName`, not `className` — `TextField.className` lands on
          // the OUTERMOST wrapper (backward-compat alias for
          // `containerClassName`), but `pr-10` clears the calendar-icon button
          // absolutely positioned over the `<input>` itself.
          inputClassName="pr-10"
        />

        {/* Calendar icon — opens the modal date dialog. Positioned inside
         *  the input on the right edge so the field reads as ONE
         *  control, matching Flutter. */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setPickerOpen(true)}
          aria-label={`Open ${label.toLowerCase()} picker`}
          className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded text-text-secondary hover:bg-grey-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CalendarIcon className="size-4" aria-hidden />
        </button>
      </div>

      {/* The Signal DS modal date dialog replaces the former local
       *  shadcn Popover + react-day-picker Calendar. Selection inside the
       *  dialog is a DRAFT and `onConfirm` (OK) is the commit point — the old
       *  popover wrote on the day click itself, with no confirm step.
       *
       *  `maxDate` carries what the old `disabled={(date) => date > (max ??
       *  new Date())}` predicate meant: no date after `max`, today by default.
       *
       *  No `onCancel` — the dialog calls `onOpenChange(false)` on CANCEL,
       *  Esc and scrim dismiss alike, so closing is already covered and a
       *  second handler would only set the same state twice.
       *
       *  The local blue "Today" link is deliberately GONE: the deployed
       *  app's dialog has no Today affordance, so keeping one would be a
       *  parity divergence, not a convenience. */}
      <DatePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        value={selectedDate}
        onConfirm={handlePickerSelect}
        maxDate={max ?? new Date()}
        captionLayout={captionLayout}
        aria-label={label}
      />

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Parsing
// ──────────────────────────────────────────────────────────────────────────

/**
 * Parse the free-text date input into an ISO timestamp (no trailing Z).
 *
 * Accepts these shapes (Flutter `DynamicDatePicker` parity):
 *   - `DD/MM/YYYY`              → exact date
 *   - `MM/YYYY`                 → first of that month
 *   - `YYYY`                    → Jan 1 of that year
 *   - `1Y` / `12M` / `30D`      → that many years/months/days BEFORE
 *                                `referenceDate` (today by default)
 *
 * Also accepts JS-native ISO (`2024-05-19`) as a fallback so a paste
 * from the calendar picker still parses.
 *
 * Returns null if nothing matches — the parent should ship onset=null
 * and rely on `onSetDisplay` to carry the user's intent for the
 * backend's dynamic-date contract.
 */
export function parseDynamicDate(
  raw: string,
  referenceDate: Date = new Date(),
): string | null {
  const text = raw.trim();
  if (!text) return null;

  // Duration: 1Y / 2M / 3D — interpret as "that long ago from now".
  // Lower-case for case-insensitivity; positive integer + unit.
  const dur = /^(\d{1,3})\s*([yYmMdD])$/.exec(text);
  if (dur) {
    const n = Number(dur[1]);
    const unit = dur[2]!.toUpperCase();
    const d = new Date(referenceDate);
    // Mutate in UTC to match `toIsoNoZ` (UTC serialization) and the
    // `Date.UTC` convention `buildDate` already uses. Local-time setters
    // here drift by the host's UTC offset and, across a DST boundary,
    // by an extra hour — the PROD-1093 regression guarded in the tests.
    if (unit === 'Y') d.setUTCFullYear(d.getUTCFullYear() - n);
    else if (unit === 'M') d.setUTCMonth(d.getUTCMonth() - n);
    else d.setUTCDate(d.getUTCDate() - n);
    // Normalize to UTC midnight so all four parse branches agree. The
    // other branches build via `buildDate` → `Date.UTC(...)` (midnight);
    // this branch starts from `referenceDate` (the caller passes a bare
    // `new Date()` — `max ?? new Date()`), so without this it would carry
    // the current time-of-day, e.g. `…T11:47:23` instead of `…T00:00:00`.
    // The wire field is a real datetime (Flutter `onsetDateTime` →
    // `toIso8601String()`), so midnight is tolerated — and the paired
    // display string (`onSetDisplay`) carries the user's actual intent.
    // PROD-1093 fast-follow.
    d.setUTCHours(0, 0, 0, 0);
    return toIsoNoZ(d);
  }

  // DD/MM/YYYY
  const ddmmyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (ddmmyyyy) {
    const [, dd, mm, yyyy] = ddmmyyyy;
    const d = buildDate(Number(yyyy), Number(mm), Number(dd));
    return d ? toIsoNoZ(d) : null;
  }

  // MM/YYYY
  const mmyyyy = /^(\d{1,2})\/(\d{4})$/.exec(text);
  if (mmyyyy) {
    const [, mm, yyyy] = mmyyyy;
    const d = buildDate(Number(yyyy), Number(mm), 1);
    return d ? toIsoNoZ(d) : null;
  }

  // YYYY
  const yyyy = /^(\d{4})$/.exec(text);
  if (yyyy) {
    const d = buildDate(Number(yyyy[1]), 1, 1);
    return d ? toIsoNoZ(d) : null;
  }

  // ISO fallback so a `2024-05-19` paste still works.
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) {
    const [, y, m, d] = iso;
    const built = buildDate(Number(y), Number(m), Number(d));
    return built ? toIsoNoZ(built) : null;
  }

  // Last resort: permissive Date.parse so "May 2024" etc. still parses.
  const fallback = new Date(text);
  return Number.isNaN(fallback.getTime()) ? null : toIsoNoZ(fallback);
}

function buildDate(year: number, month: number, day: number): Date | null {
  if (year < 1900 || year > 2100) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  // Use UTC midnight so the resulting ISO doesn't drift due to
  // timezone offsets when the user is east-of-UTC.
  const d = new Date(Date.UTC(year, month - 1, day));
  // Reject "Feb 31" — Date silently rolls forward; check round-trip.
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null;
  }
  return d;
}

/**
 * ISO `YYYY-MM-DDTHH:mm:ss.sss` with the trailing `Z` stripped — the
 * format the .NET model binder expects on every chart-tab onset/end date field.
 *
 * ⚠️ **PROD-1778 — do NOT copy this implementation elsewhere.** In the general
 * case `toISOString().replace(/Z$/, '')` is a FALSE safeguard: `toISOString()`
 * converts the instant to UTC first, so removing the `Z` bakes the tz offset in
 * rather than removing it, and the backend stores the shifted literal. The
 * canonical helper for a wall-clock instant is
 * `formatLocalDatetimeForWire` in `@/shared/lib/date-format`.
 *
 * It is kept here because THIS module is UTC-internal by contract: every caller
 * hands in a Date built via `Date.UTC(...)` (`buildDate` above), and the sibling
 * `formatDdMmYyyy` reads `getUTC*` too, so UTC serialization round-trips exactly
 * and the calendar day is preserved in any timezone. Switching this one function
 * to local components would break that round-trip.
 *
 * Known soft spot: the permissive `new Date(text)` fallback at the bottom of
 * `parseDateText` produces a LOCAL-midnight Date, which this function then reads
 * in UTC — so a free-text value like "May 2024" can land a day early west of
 * UTC. Out of scope for PROD-1778; it only affects the last-resort parse branch.
 */
export function toIsoNoZ(d: Date): string {
  // PROD-1778: the ONE sanctioned use of this shape. Every caller in this module
  // builds its Date via `Date.UTC`, so reading it back in UTC is a lossless
  // round-trip, not a tz shift (see docblock). New code: formatLocalDatetimeForWire.
  // Tracked for a real fix + rename in PROD-1787.
  // eslint-disable-next-line no-restricted-syntax -- UTC-internal by contract (see docblock)
  return d.toISOString().replace(/Z$/, '');
}

/** "DD/MM/YYYY" — Flutter's `endDisplayDate` wire format. */
export function formatDdMmYyyy(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}
